import { useState, useEffect, useRef } from "react";
import {
  Box,
  SpaceBetween,
  Table as CSDTable,
  Header,
  Spinner,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { useAsyncCallback } from "react-async-hook";

import { COMMON_LABELS } from "../default-constants";
import { CONFIGS, LABELS } from "./constants";
import { useAppDispatch } from "../../app/hook";
import { showModal } from "../UI/modal/modal-slice";
import {
  useGetDependenceCharactersQuery,
  usePostDependenceCharactersUploadMutation,
  useLazyGetDependenceCharactersDownloadQuery,
  usePostDependenceCharactersMutation,
} from "../../features/api";
import { formatLabel } from "../../utils/format";
import { Messages } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { IconButton } from "../UI/button/IconButton";

/*
 * 環境依存文字マッピング定義照会・更新画面の一覧表
 */
const Table = () => {
  // ダウンロード開始
  let { data, isFetching, refetch } = useGetDependenceCharactersQuery();
  // ダウンロード実行
  const [lazyDownloadDependenceCharacterTriggers] = useLazyGetDependenceCharactersDownloadQuery();
  const dispatch = useAppDispatch();
  const [uploadEnvMapFile] = usePostDependenceCharactersUploadMutation();
  // 環境依存文字マッピング定義更新API
  const [updateDependenceCharacters] = usePostDependenceCharactersMutation();
  const inputRef = useRef<HTMLInputElement>(null);
  // spinnerと文字列表示有無
  const [isDownloadSpinnerAndStrDisplay, setIsDownloadSpinnerAndStrDisplay] = useState<boolean>(false);
  // button表示有無
  const [isDependenceCharactersDownloadButton, setIsDependenceCharactersDownloadButton] = useState<boolean>(false);

  /**
   * DLボタンを押下イベント関数
   */
  const onClickDownloadBtnHandler = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    executeDownload();
  };

  const executeDownload = async () => {
    // spinnerと文字列活性
    setIsDownloadSpinnerAndStrDisplay(true);
    // button活性
    setIsDependenceCharactersDownloadButton(true);
    await lazyDownloadDependenceCharacterTriggers(undefined)
      .unwrap()
      .then()
      .finally(() => {
        // spinnerと文字列非活性
        setIsDownloadSpinnerAndStrDisplay(false);
        // button非活性
        setIsDependenceCharactersDownloadButton(false);
      });
  }

  // アップロードボタンイベント
  const uploadOnClick = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    // ファイルアップロード処理
    inputRef.current?.click();
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  const onChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!(files && files[0])) {
      return;
    }

    // アップロードされたファイルの拡張子が「.csv」以外の場合はアラートを出して終了する。
    if (CONFIGS.FILE_EXTENTION !== getExt(files[0].name.toLowerCase())) {
      const messageId = "KGRERR008" as keyof Messages;
      // アラート処理
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [],
        })
      );
      return;
    }

    const fileData = new FormData();
    fileData.append(CONFIGS.FILE, files[0]);

    // 環境依存文字マッピング定義ファイルアップロードAPI呼び出し
    const checkData = await uploadEnvMapFile(fileData).unwrap();

    if (CONFIGS.SUCCSESS === checkData.status) {
      // モーダル表示処理
      let args: number[] = [];
      args = [...args, ...Object.values(checkData.checkResult)];
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.UPDATE,
          content: formatLabel(LABELS.UPLOAD_CONFIIM as string, args),
          // 環境依存文字マッピング定義更新API呼び出し
          positiveFn: () =>
            updateDependenceCharacters(checkData.updateData).then(() =>
              refetch()
            ),
        })
      );
    }

    /** 同じファイルを連続アップロードできるように処理 */
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  //ファイル名から拡張子を取得する関数
  function getExt(filename: string) {
    let pos = filename.lastIndexOf(".");
    if (pos === -1) return "";
    return filename.slice(pos);
  }

  /**
   * アップロードファイルが選択されるイベント
   */
  const asyncEvent = useAsyncCallback(onChangeFile);

  const columnDefinitions = [
    {
      id: "dependenceCharacter",
      header: LABELS.DEPENDENCE_CHARACTER,
      cell: (e: any) => e.dependenceCharacter,
    },
    {
      id: "convertedCharacter",
      header: LABELS.CONVERTED_CHARACTER,
      cell: (e: any) => e.convertedCharacter,
    },
  ];

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  useEffect(() => {
    if (data !== undefined) {
      setPagedItems(getPagedItems(data, 1, DEFAULT_PAGE_SIZE));
      setAllItems(data);
      setCurrentPageIndex(1);
    }
  }, [data]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (data !== undefined) {
      setPagedItems(data.slice(0, newPageSize));
      setCurrentPageIndex(1);
    }
  };

  if (!data) return null;

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      loading={isFetching}
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      items={pagedItems}
      resizableColumns
      stickyHeader
      totalItemsCount={13}
      header={
        <Header
          actions={
            <SpaceBetween size={"xxl"} direction={"horizontal"}>
               
              <IconButton
                iconName={"upload"}
                label={COMMON_LABELS.BTNS.UL}
                onClick={uploadOnClick}
              />
              <SpaceBetween direction="horizontal" size="xs">
                {isDownloadSpinnerAndStrDisplay ? <Spinner size="big" /> : <></>}
                {isDownloadSpinnerAndStrDisplay ? <>{COMMON_LABELS.CONTENTS.DL_RUN}</> : <></>}
                  <IconButton
                    iconName={"download"}
                    label={COMMON_LABELS.BTNS.DL}
                    disabled={isDependenceCharactersDownloadButton}
                    onClick={onClickDownloadBtnHandler}
                  />
              </SpaceBetween>
              <input
                hidden
                ref={inputRef}
                name="file"
                type="file"
                accept=".csv"
                onChange={asyncEvent.execute}
              />
            </SpaceBetween>
          }
        >
          {LABELS.TITLE}
          {getHeaderCounterText(allItems.length, pagedItems.length)}
        </Header>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default Table;
